import React, { useCallback, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../services/firebase";
import { signOut } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { COLORS, LAYOUT } from "../constants";
import "./Navbar.css";

const Navbar = React.memo(({ user, needsVerify }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [todayEventsCount, setTodayEventsCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationListenersLoaded, setNotificationListenersLoaded] = useState(false);
  const isWelcomePage = location.pathname === '/' || location.pathname === '/welcome';

  // Scroll detection for welcome page
  useEffect(() => {
    if (!isWelcomePage) {
      setIsScrolled(false);
      return;
    }

    const handleScroll = () => {
      // Hero section is 100vh, trigger exactly when leaving the red section
      const scrollThreshold = window.innerHeight - 72; // Subtract navbar height for perfect timing
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > scrollThreshold);
    };

    // Set initial state
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isWelcomePage]);

  // Listen for unread messages
  useEffect(() => {
    if (!user) {
      setUnreadMessagesCount(0);
      return;
    }

    const messagesQuery = query(
      collection(db, 'messages'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      // Count threads where the last message wasn't sent by the current user
      // and hasn't been read by them
      let count = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // If there's a lastMessage and it wasn't sent by current user
        if (data.lastMessage && data.lastMessageSender && data.lastMessageSender !== user.uid) {
          // Check if current user has read it
          const hasRead = data.readBy && data.readBy.includes(user.uid);
          if (!hasRead) {
            count++;
            } else {
            }
        } else if (data.lastMessage && !data.lastMessageSender) {
          } else if (!data.lastMessage) {
          } else if (data.lastMessageSender === user.uid) {
          }
      });
      setUnreadMessagesCount(count);
      // Mark messages listener as loaded after first snapshot
      setNotificationListenersLoaded(prev => prev || true);
    }, (error) => {
      });

    return () => unsubscribe();
  }, [user, location.pathname]); // Re-run when location changes

  // Listen for unread notifications
  useEffect(() => {
    if (!user) {
      setUnreadNotificationsCount(0);
      return;
    }

    // Get last notifications visit timestamp from localStorage
    const lastVisit = localStorage.getItem(`lastNotificationsVisit_${user.uid}`);
    const lastVisitTime = lastVisit ? new Date(lastVisit).getTime() : 0;

    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      // Count notifications that are unread/pending AND created after last visit
      let count = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const notificationCreatedAt = data.createdAt?.toMillis() || 0;
        
        // Count if: (unread OR pending OR rejected) AND created after last visit AND not archived
        if ((data.status === 'unread' || data.status === 'pending' || data.status === 'rejected') && 
            data.status !== 'archived' &&
            notificationCreatedAt > lastVisitTime) {
          count++;
        }
      });
      setUnreadNotificationsCount(count);
    }, (error) => {
    });

    return () => unsubscribe();
  }, [user, location.pathname]); // Re-run when location changes

  // Listen for calendar events (new events since last visit)
  useEffect(() => {
    if (!user) {
      setTodayEventsCount(0);
      return;
    }

    // Get last calendar visit timestamp from localStorage (re-read on location changes)
    const lastVisit = localStorage.getItem(`lastCalendarVisit_${user.uid}`);
    const lastVisitTime = lastVisit ? new Date(lastVisit).getTime() : 0;

    const calendarQuery = query(
      collection(db, 'calendarEvents'),
      where('buyerId', '==', user.uid)
    );

    const calendarQuery2 = query(
      collection(db, 'calendarEvents'),
      where('sellerId', '==', user.uid)
    );

    let totalCount = 0;
    let unsubscribe1, unsubscribe2;

    const updateCount = () => {
      setTodayEventsCount(totalCount);
    };

    // Listen to buyer events
    unsubscribe1 = onSnapshot(calendarQuery, (snapshot) => {
      let buyerCount = 0;
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        // Count new events since last visit
        const eventCreatedAt = data.createdAt?.toMillis() || 0;
        if (eventCreatedAt > lastVisitTime) {
          buyerCount++;
        }
      });
      
      // Listen to seller events
      unsubscribe2 = onSnapshot(calendarQuery2, (snapshot2) => {
        let sellerCount = 0;
        snapshot2.docs.forEach(doc => {
          const data = doc.data();
          // Count new events since last visit
          const eventCreatedAt = data.createdAt?.toMillis() || 0;
          if (eventCreatedAt > lastVisitTime) {
            sellerCount++;
          }
        });
        
        totalCount = buyerCount + sellerCount;
        updateCount();
      }, (error) => {
      });
    }, (error) => {
    });

    return () => {
      if (unsubscribe1) unsubscribe1();
      if (unsubscribe2) unsubscribe2();
    };
  }, [user, location.pathname]); // Re-run when location changes

  // Debug: Log all notification counts
  useEffect(() => {
    }, [unreadMessagesCount, unreadNotificationsCount, todayEventsCount]);

  // TEMPORARY: Force test notifications to see if dots show
  useEffect(() => {
    if (user) {
      // Uncomment to test notification dots:
      // setUnreadMessagesCount(1);
      // setUnreadNotificationsCount(1);  
      // setTodayEventsCount(1);
    }
  }, [user]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      alert("You have been logged out successfully.");
      setIsMobileMenuOpen(false); // Close mobile menu on logout
      navigate("/");
    } catch (error) {
      alert("Unable to log out. Please try again.");
    }
  }, [navigate]);

  const goToHome = useCallback(() => {
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
    if (user && user.emailVerified) {
      navigate("/market");
    } else {
      navigate("/");
    }
  }, [user, navigate]);

  // Dynamic styles based on scroll state and page
  const getNavbarStyles = () => {
    if (isWelcomePage) {
      return isScrolled ? styles.navbar : styles.navbarTransparent;
    }
    return styles.navbar;
  };

  const getTextColor = () => {
    if (isWelcomePage && !isScrolled) {
      return COLORS.text.primary; // Dark text on white navbar
    }
    return COLORS.white; // White text on red navbar
  };

  const getLogoColor = () => {
    if (isWelcomePage && !isScrolled) {
      return COLORS.primary; // Red logo on white navbar
    }
    return COLORS.white; // White logo on red navbar
  };

  const getLogoStyles = () => ({
    ...styles.logo,
    color: getLogoColor(),
  });

  const getLinkStyles = () => ({
    ...styles.link,
    color: getTextColor(),
  });

  const getLinksContainerStyles = () => {
    if (isWelcomePage && !isScrolled) {
      return {
        ...styles.links,
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
      };
    }
    return styles.links;
  };

  const getLogoutButtonStyles = () => {
    if (isWelcomePage && !isScrolled) {
      return {
        ...styles.logoutButton,
        backgroundColor: COLORS.primary,
        color: COLORS.white,
        border: `2px solid ${COLORS.primary}`,
      };
    }
    return styles.logoutButton;
  };

  const getLinkHoverHandlers = () => ({
    onMouseOver: (e) => {
      const hoverBg = isWelcomePage && !isScrolled ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)';
      e.target.style.backgroundColor = hoverBg;
      e.target.style.transform = 'translateY(-1px)';
      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
    },
    onMouseOut: (e) => {
      e.target.style.backgroundColor = 'transparent';
      e.target.style.transform = 'translateY(0)';
      e.target.style.boxShadow = 'none';
    }
  });

  const getLogoutHoverHandlers = () => ({
    onMouseOver: (e) => {
      if (isWelcomePage && !isScrolled) {
        // On white navbar: hover to darker red
        e.target.style.backgroundColor = '#9d0d1d';
        e.target.style.color = COLORS.white;
        e.target.style.borderColor = '#9d0d1d';
      } else {
        // On red navbar: hover to slightly darker white with red text
        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
        e.target.style.color = COLORS.primary;
        e.target.style.borderColor = COLORS.white;
      }
      e.target.style.transform = 'translateY(-2px)';
      e.target.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
    },
    onMouseOut: (e) => {
      const defaultStyles = getLogoutButtonStyles();
      e.target.style.backgroundColor = defaultStyles.backgroundColor;
      e.target.style.color = defaultStyles.color;
      e.target.style.borderColor = defaultStyles.border.split(' ')[2]; // Extract color from border
      e.target.style.transform = 'translateY(0)';
      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
    }
  });

  const getNotificationDotStyles = () => ({
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '8px',
    height: '8px',
    backgroundColor: COLORS.white, // White dot for all notifications
    borderRadius: '50%',
    border: '2px solid ' + (isWelcomePage && !isScrolled ? COLORS.primary : 'rgba(255, 255, 255, 0.3)'),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    animation: 'pulse 2s infinite',
    zIndex: 1,
  });

  const getMobileMenuButtonStyles = () => ({
    // Base styles, display controlled by CSS media queries
    position: 'relative',
  });

  const getHamburgerLineStyles = (index, isOpen) => {
    const baseColor = isWelcomePage && !isScrolled ? COLORS.primary : COLORS.white;
    const baseStyle = {
      width: '100%',
      height: '3px',
      backgroundColor: baseColor,
      borderRadius: '3px',
      transition: 'all 0.3s ease',
      transformOrigin: 'center',
      position: 'absolute',
      left: 0,
    };

    if (isOpen) {
      if (index === 0) {
        return { ...baseStyle, top: '50%', transform: 'translateY(-50%) rotate(45deg)' };
      } else if (index === 1) {
        return { ...baseStyle, opacity: 0, top: '50%' };
      } else {
        return { ...baseStyle, top: '50%', transform: 'translateY(-50%) rotate(-45deg)' };
      }
    }

    // Closed state positions
    if (index === 0) {
      return { ...baseStyle, top: '0' };
    } else if (index === 1) {
      return { ...baseStyle, top: '50%', transform: 'translateY(-50%)' };
    } else {
      return { ...baseStyle, bottom: '0' };
    }
  };

  const getMobileOverlayStyles = () => ({
    position: 'fixed',
    top: '72px',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: isWelcomePage && !isScrolled ? 'rgba(255, 255, 255, 0.98)' : COLORS.primary,
    zIndex: 1050,
    overflowY: 'auto',
    animation: 'slideDown 0.3s ease',
    backdropFilter: 'blur(10px)',
    // display controlled by CSS media queries via className
  });

  const getMobileLinksStyles = () => ({
    listStyle: 'none',
    padding: LAYOUT.spacing.xl,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.sm,
  });

  const getMobileLinkStyles = () => ({
    color: isWelcomePage && !isScrolled ? COLORS.text.primary : COLORS.white,
    textDecoration: 'none',
    fontSize: '1.1rem',
    fontWeight: '600',
    padding: LAYOUT.spacing.lg,
    borderRadius: LAYOUT.borderRadius.md,
    display: 'block',
    transition: 'all 0.2s ease',
    backgroundColor: isWelcomePage && !isScrolled ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.1)',
    border: isWelcomePage && !isScrolled ? '1px solid rgba(0, 0, 0, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
  });

  const getMobileLogoutButtonStyles = () => ({
    width: '100%',
    backgroundColor: isWelcomePage && !isScrolled ? COLORS.primary : COLORS.white,
    color: isWelcomePage && !isScrolled ? COLORS.white : COLORS.primary,
    border: 'none',
    borderRadius: LAYOUT.borderRadius.md,
    padding: LAYOUT.spacing.lg,
    fontSize: '1.1rem',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: LAYOUT.spacing.md,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  });

  return (
    <nav style={getNavbarStyles()}>
      <div style={styles.container}>
        <div 
          style={getLogoStyles()} 
          onClick={goToHome}
          onMouseOver={(e) => {
            e.target.style.transform = 'scale(1.02)';
            e.target.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.2)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'scale(1)';
            e.target.style.textShadow = 'none';
          }}
        >
          RedBird Markets
        </div>
        
        {/* Mobile menu button */}
        {user && (
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={getMobileMenuButtonStyles()}
            className="mobile-menu-button"
            aria-label="Toggle menu"
          >
            <div style={getHamburgerLineStyles(0, isMobileMenuOpen)}></div>
            <div style={getHamburgerLineStyles(1, isMobileMenuOpen)}></div>
            <div style={getHamburgerLineStyles(2, isMobileMenuOpen)}></div>
          </button>
        )}
        
        <div style={styles.navigation} className="desktop-navigation">
          {user && (
            <ul style={getLinksContainerStyles()}>
              <li>
                <Link 
                  to="/seller-dashboard" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link 
                  to="/add-listing" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  Sell
                </Link>
              </li>
              <li>
                <Link 
                  to="/my-listings" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  My Items
                </Link>
              </li>
              <li>
                <Link 
                  to="/transactions" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  Orders
                </Link>
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/direct" 
                  style={getLinkStyles()}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Clear messages notification when visiting messages page
                    if (user) {
                      localStorage.setItem(`lastMessagesVisit_${user.uid}`, new Date().toISOString());
                    }
                  }}
                  {...getLinkHoverHandlers()}
                >
                  Messages
                </Link>
                {notificationListenersLoaded && unreadMessagesCount > 0 && (
                  <span style={getNotificationDotStyles()}></span>
                )}
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/notifications" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  Notifications
                </Link>
                {notificationListenersLoaded && unreadNotificationsCount > 0 && (
                  <span style={getNotificationDotStyles()}></span>
                )}
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/calendar" 
                  style={getLinkStyles()}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Clear calendar notification when visiting calendar page
                    if (user) {
                      localStorage.setItem(`lastCalendarVisit_${user.uid}`, new Date().toISOString());
                    }
                  }}
                  {...getLinkHoverHandlers()}
                >
                  Calendar
                </Link>
                {notificationListenersLoaded && todayEventsCount > 0 && (
                  <span style={getNotificationDotStyles()}></span>
                )}
              </li>
              <li>
                <Link 
                  to="/profile" 
                  style={getLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                  {...getLinkHoverHandlers()}
                >
                  Profile
                </Link>
              </li>
            </ul>
          )}
          
          {user && (
            <button 
              onClick={handleLogout} 
              style={getLogoutButtonStyles()}
              {...getLogoutHoverHandlers()}
            >
              Sign Out
            </button>
          )}
        </div>

        {/* Mobile navigation overlay */}
        {user && isMobileMenuOpen && (
          <div style={getMobileOverlayStyles()} className="mobile-overlay">
            <ul style={getMobileLinksStyles()}>
              <li>
                <Link 
                  to="/seller-dashboard" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link 
                  to="/add-listing" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sell
                </Link>
              </li>
              <li>
                <Link 
                  to="/my-listings" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  My Items
                </Link>
              </li>
              <li>
                <Link 
                  to="/transactions" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Orders
                </Link>
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/direct" 
                  style={getMobileLinkStyles()}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Clear messages notification when visiting messages page
                    if (user) {
                      localStorage.setItem(`lastMessagesVisit_${user.uid}`, new Date().toISOString());
                    }
                  }}
                >
                  Messages
                  {notificationListenersLoaded && unreadMessagesCount > 0 && (
                    <span style={{
                      ...styles.mobileBadge,
                      color: COLORS.white,
                      marginLeft: '8px'
                    }}>●</span>
                  )}
                </Link>
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/notifications" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Notifications
                  {notificationListenersLoaded && unreadNotificationsCount > 0 && (
                    <span style={{
                      ...styles.mobileBadge,
                      color: COLORS.white,
                      marginLeft: '8px'
                    }}>●</span>
                  )}
                </Link>
              </li>
              <li style={{position: 'relative'}}>
                <Link 
                  to="/calendar" 
                  style={getMobileLinkStyles()}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    // Clear calendar notification when visiting calendar page
                    if (user) {
                      localStorage.setItem(`lastCalendarVisit_${user.uid}`, new Date().toISOString());
                    }
                  }}
                >
                  Calendar
                  {notificationListenersLoaded && todayEventsCount > 0 && (
                    <span style={{
                      ...styles.mobileBadge,
                      color: COLORS.white,
                      marginLeft: '8px'
                    }}>●</span>
                  )}
                </Link>
              </li>
              <li>
                <Link 
                  to="/profile" 
                  style={getMobileLinkStyles()}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </Link>
              </li>
              <li>
                <button 
                  onClick={handleLogout} 
                  style={getMobileLogoutButtonStyles()}
                >
                  Sign Out
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </nav>
  );
});

const styles = {
  navbar: {
    backgroundColor: COLORS.primary,
    boxShadow: '0 2px 8px rgba(206, 17, 38, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    minHeight: '72px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  navbarTransparent: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.08)',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    minHeight: '72px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${LAYOUT.spacing.lg} ${LAYOUT.spacing.xl}`,
    minHeight: '72px',
    position: 'relative',
  },
  logo: {
    color: COLORS.white,
    cursor: 'pointer',
    fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', // Responsive font size
    fontWeight: '800',
    letterSpacing: '-0.025em',
    textTransform: 'none',
    margin: 0,
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    },
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.xxl,
  },
  links: {
    display: 'flex',
    gap: '2px',
    alignItems: 'center',
    listStyle: 'none',
    margin: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: LAYOUT.borderRadius.lg,
    padding: LAYOUT.spacing.xs,
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  link: {
    color: COLORS.white,
    textDecoration: 'none',
    fontSize: 'clamp(0.8rem, 1.5vw, 0.9rem)', // Responsive font size
    fontWeight: '500',
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
    borderRadius: LAYOUT.borderRadius.md,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    whiteSpace: 'nowrap',
    position: 'relative',
    letterSpacing: '0.025em',
    display: 'block',
  },
  logoutButton: {
    backgroundColor: COLORS.white,
    color: COLORS.primary,
    border: `2px solid ${COLORS.white}`,
    borderRadius: LAYOUT.borderRadius.md,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    fontSize: 'clamp(0.85rem, 1.5vw, 0.95rem)', // Responsive font size
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    letterSpacing: '0.025em',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: COLORS.accent,
      color: COLORS.text.onYellow,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    },
  },
  mobileBadge: {
    color: '#22c55e',
    fontSize: '1.5rem',
    marginLeft: '8px',
    verticalAlign: 'middle',
  },
};

export default Navbar;
